/**
 * Centralized status color mapping for consistent visual representation
 * across all views in the Initiative Tracker application
 */

export type StatusColorType = 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' | 'default'

export interface StatusColorMapping {
  chipColor: StatusColorType
  hexColor: string
  hexColorDark: string
}

/**
 * Standardized status color mappings based on requirements:
 * - Backlog: Grey background
 * - In Progress: Light blue background  
 * - Awaiting Verification: Green background
 * - Paused: Orange background
 * - Prioritized: Purple background
 */
const STATUS_COLOR_MAP: Record<string, StatusColorMapping> = {
  // Triage/Backlog statuses - Grey
  'triage': {
    chipColor: 'default',
    hexColor: '#9e9e9e',
    hexColorDark: '#bdbdbd'
  },
  'backlog': {
    chipColor: 'default',
    hexColor: '#9e9e9e',
    hexColorDark: '#bdbdbd'
  },
  'to do': {
    chipColor: 'default',
    hexColor: '#9e9e9e',
    hexColorDark: '#bdbdbd'
  },
  'planning': {
    chipColor: 'default',
    hexColor: '#9e9e9e',
    hexColorDark: '#bdbdbd'
  },

  // In Progress statuses - Light blue
  'in progress': {
    chipColor: 'primary',
    hexColor: '#2196f3',
    hexColorDark: '#42a5f5'
  },
  'progress': {
    chipColor: 'primary',
    hexColor: '#2196f3',
    hexColorDark: '#42a5f5'
  },
  'active': {
    chipColor: 'primary',
    hexColor: '#2196f3',
    hexColorDark: '#42a5f5'
  },

  // Awaiting Verification statuses - Green
  'awaiting verification': {
    chipColor: 'success',
    hexColor: '#4caf50',
    hexColorDark: '#66bb6a'
  },
  'verification': {
    chipColor: 'success',
    hexColor: '#4caf50',
    hexColorDark: '#66bb6a'
  },
  'review': {
    chipColor: 'success',
    hexColor: '#4caf50',
    hexColorDark: '#66bb6a'
  },

  // Paused statuses - Orange
  'paused': {
    chipColor: 'warning',
    hexColor: '#ff9800',
    hexColorDark: '#ffb74d'
  },
  'hold': {
    chipColor: 'warning',
    hexColor: '#ff9800',
    hexColorDark: '#ffb74d'
  },
  'on hold': {
    chipColor: 'warning',
    hexColor: '#ff9800',
    hexColorDark: '#ffb74d'
  },

  // Prioritized statuses - Purple
  'prioritized': {
    chipColor: 'secondary',
    hexColor: '#9c27b0',
    hexColorDark: '#ab47bc'
  },
  'ready': {
    chipColor: 'secondary',
    hexColor: '#9c27b0',
    hexColorDark: '#ab47bc'
  },

  // Completed statuses - Green (same as verification for consistency)
  'done': {
    chipColor: 'success',
    hexColor: '#4caf50',
    hexColorDark: '#66bb6a'
  },
  'complete': {
    chipColor: 'success',
    hexColor: '#4caf50',
    hexColorDark: '#66bb6a'
  },
  'closed': {
    chipColor: 'success',
    hexColor: '#4caf50',
    hexColorDark: '#66bb6a'
  },

  // Blocked/Error statuses - Red
  'blocked': {
    chipColor: 'error',
    hexColor: '#f44336',
    hexColorDark: '#ef5350'
  },

  // Special status indicators
  'overdue': {
    chipColor: 'error',
    hexColor: '#f44336',
    hexColorDark: '#ef5350'
  },
  'at-risk': {
    chipColor: 'warning',
    hexColor: '#ff9800',
    hexColorDark: '#ffb74d'
  },
  'warning': {
    chipColor: 'warning',
    hexColor: '#ff7043',
    hexColorDark: '#ff8a65'
  },

  // Prominence levels for timeline bars
  'ultra': {
    chipColor: 'error',
    hexColor: '#f44336',
    hexColorDark: '#ef5350'
  },
  'very-high': {
    chipColor: 'warning',
    hexColor: '#ff9800',
    hexColorDark: '#ffb74d'
  },
  'high': {
    chipColor: 'primary',
    hexColor: '#2196f3',
    hexColorDark: '#42a5f5'
  },
  'medium': {
    chipColor: 'secondary',
    hexColor: '#9c27b0',
    hexColorDark: '#ab47bc'
  },
  'standard': {
    chipColor: 'default',
    hexColor: '#757575',
    hexColorDark: '#9e9e9e'
  }
}

/**
 * Get Material-UI chip color for a status
 * @param status - The status string (case insensitive)
 * @returns Material-UI color type for Chip component
 */
export const getStatusChipColor = (status: string): StatusColorType => {
  if (!status) return 'default'
  
  const statusLower = status.toLowerCase().trim()
  
  // Direct match first
  if (STATUS_COLOR_MAP[statusLower]) {
    return STATUS_COLOR_MAP[statusLower].chipColor
  }
  
  // Partial match for complex status names
  for (const [key, mapping] of Object.entries(STATUS_COLOR_MAP)) {
    if (statusLower.includes(key)) {
      return mapping.chipColor
    }
  }
  
  // Default fallback
  return 'default'
}

/**
 * Get hex color for a status (theme-aware)
 * @param status - The status string (case insensitive)
 * @param isDarkMode - Whether dark mode is active
 * @returns Hex color string
 */
export const getStatusHexColor = (status: string, isDarkMode: boolean = false): string => {
  if (!status) return isDarkMode ? '#bdbdbd' : '#9e9e9e'
  
  const statusLower = status.toLowerCase().trim()
  
  // Direct match first
  if (STATUS_COLOR_MAP[statusLower]) {
    return isDarkMode ? STATUS_COLOR_MAP[statusLower].hexColorDark : STATUS_COLOR_MAP[statusLower].hexColor
  }
  
  // Partial match for complex status names
  for (const [key, mapping] of Object.entries(STATUS_COLOR_MAP)) {
    if (statusLower.includes(key)) {
      return isDarkMode ? mapping.hexColorDark : mapping.hexColor
    }
  }
  
  // Default fallback
  return isDarkMode ? '#bdbdbd' : '#9e9e9e'
}

/**
 * List of status names that indicate an epic or initiative is inactive/closed
 * These statuses should be excluded from active workload calculations
 */
export const INACTIVE_STATUSES = [
  'done',
  'closed',
  'resolved',
  'cancelled',
  'canceled', // Alternative spelling
  'complete',
  'completed',
  'finished',
  'rejected',
  'abandoned',
  'obsolete',
  'duplicate',
  'won\'t do',
  'wont do',
  'will not do'
] as const

/**
 * Determine if an epic or initiative status indicates it is active (not closed/completed)
 * @param status - The status string (case insensitive)
 * @returns true if the status indicates active work, false if inactive/closed
 */
export const isActiveStatus = (status: string): boolean => {
  if (!status) return false

  const statusLower = status.toLowerCase().trim()

  // Check for exact matches with inactive statuses
  if (INACTIVE_STATUSES.some(inactiveStatus => statusLower === inactiveStatus)) {
    return false
  }

  // Check for partial matches (e.g., "Done - Verified", "Closed - Duplicate")
  if (INACTIVE_STATUSES.some(inactiveStatus => statusLower.includes(inactiveStatus))) {
    return false
  }

  // If not in inactive list, consider it active
  return true
}

/**
 * Filter child epics to only include those with active statuses
 * @param childEpics - Array of child epic objects with status information
 * @returns Array of child epics with active statuses only
 */
export const filterActiveChildEpics = <T extends { status?: { name?: string } | string }>(
  childEpics: T[]
): T[] => {
  return childEpics.filter(epic => {
    // Handle different status formats
    const status = typeof epic.status === 'string'
      ? epic.status
      : epic.status?.name || ''

    return isActiveStatus(status)
  })
}

/**
 * Get status color mapping object
 * @param status - The status string (case insensitive)
 * @returns Complete status color mapping or default
 */
export const getStatusColorMapping = (status: string): StatusColorMapping => {
  if (!status) {
    return {
      chipColor: 'default',
      hexColor: '#9e9e9e',
      hexColorDark: '#bdbdbd'
    }
  }

  const statusLower = status.toLowerCase().trim()

  // Direct match first
  if (STATUS_COLOR_MAP[statusLower]) {
    return STATUS_COLOR_MAP[statusLower]
  }

  // Partial match for complex status names
  for (const [key, mapping] of Object.entries(STATUS_COLOR_MAP)) {
    if (statusLower.includes(key)) {
      return mapping
    }
  }

  // Default fallback
  return {
    chipColor: 'default',
    hexColor: '#9e9e9e',
    hexColorDark: '#bdbdbd'
  }
}

/**
 * Get color for special status indicators (warning, at-risk, overdue, etc.)
 * @param indicatorType - The indicator type (warning, at-risk, overdue, etc.)
 * @param isDarkMode - Whether dark mode is active
 * @returns Hex color string
 */
export const getIndicatorColor = (indicatorType: string, isDarkMode: boolean = false): string => {
  return getStatusHexColor(indicatorType, isDarkMode)
}

/**
 * Get color for prominence levels (ultra, very-high, high, medium, standard)
 * @param prominenceLevel - The prominence level
 * @param isDarkMode - Whether dark mode is active
 * @returns Hex color string
 */
export const getProminenceColor = (prominenceLevel: string, isDarkMode: boolean = false): string => {
  return getStatusHexColor(prominenceLevel, isDarkMode)
}

/**
 * Convert hex color to lighter shade for progress bar backgrounds
 * @param hexColor - The base hex color (with or without #)
 * @param opacity - Opacity level for the lighter shade (0-1, default: 0.3)
 * @returns Hex color with alpha channel (rgba format)
 */
export const getLighterStatusColor = (hexColor: string, opacity: number = 0.3): string => {
  // Remove # if present
  const hex = hexColor.replace('#', '')

  // Parse RGB values
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)

  // Return rgba with specified opacity
  return `rgba(${r}, ${g}, ${b}, ${opacity})`
}

/**
 * Get lighter shade of status color for progress bar backgrounds
 * @param status - The status string (case insensitive)
 * @param isDarkMode - Whether dark mode is active
 * @param opacity - Opacity level for the lighter shade (0-1, default: 0.3)
 * @returns Hex color with alpha channel (rgba format)
 */
export const getLighterStatusColorForStatus = (
  status: string,
  isDarkMode: boolean = false,
  opacity: number = 0.3
): string => {
  const baseColor = getStatusHexColor(status, isDarkMode)
  return getLighterStatusColor(baseColor, opacity)
}

/**
 * Legacy compatibility function for existing getStatusColor usage
 * @param status - The status string
 * @returns Material-UI color type
 */
export const getStatusColor = getStatusChipColor
